import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth, ProtectedRoute } from '../../components/auth/AuthContext'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  image: string
  category: string
  author: string
  date: string
  readTime: number
  published: boolean
  seo?: {
    metaTitle?: string
  }
}

interface AnalyticsSummary {
  [postId: string]: {
    total: number
    today: number
    trend: 'up' | 'down' | 'stable'
  }
}

function ArticlesContent() {
  const { email, logout, token } = useAuth()
  const navigate = useNavigate()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [analytics, setAnalytics] = useState<AnalyticsSummary>({})
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchPosts()
    fetchAnalytics()
  }, [])

  const fetchPosts = async () => {
    try {
      const response = await fetch('/api/posts/all')
      const data = await response.json()
      setPosts(data)
    } catch (error) {
      console.error('Error fetching posts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAnalytics = async () => {
    try {
      const response = await fetch('/api/analytics')
      const data = await response.json()
      // API returns an object with postId as keys
      const analyticsMap: AnalyticsSummary = {}
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Data is already in the format { postId: { total, today, last7Days } }
        for (const [postId, stats] of Object.entries(data)) {
          const s = stats as { total: number; today: number; last7Days: number }
          analyticsMap[postId] = {
            total: s.total,
            today: s.today,
            trend: s.today > 0 ? 'up' : 'stable'
          }
        }
      }
      setAnalytics(analyticsMap)
    } catch (error) {
      console.error('Error fetching analytics:', error)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/admin/login')
  }

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Sei sicuro di voler eliminare "${title}"?`)) return

    try {
      await fetch(`/api/posts/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setPosts(posts.filter(p => p.id !== id))
    } catch (error) {
      console.error('Error deleting post:', error)
    }
  }

  const handleTogglePublish = async (post: BlogPost) => {
    try {
      const response = await fetch(`/api/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ published: !post.published }),
      })
      const updated = await response.json()
      setPosts(posts.map(p => (p.id === post.id ? { ...p, published: updated.published } : p)))
    } catch (error) {
      console.error('Error updating post:', error)
    }
  }

  // Filter posts
  const filteredPosts = posts.filter(post => {
    // Status filter
    if (filter === 'published' && !post.published) return false
    if (filter === 'draft' && post.published) return false
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        post.title.toLowerCase().includes(query) ||
        post.slug.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query)
      )
    }
    
    return true
  })

  const stats = {
    total: posts.length,
    published: posts.filter(p => p.published).length,
    draft: posts.filter(p => !p.published).length,
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link to="/admin" className="text-gray-500 hover:text-gray-700">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h1 className="text-xl font-bold text-gray-900">Gestione Articoli</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{email}</span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="text-sm text-gray-500">Totale Articoli</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="text-sm text-gray-500">Pubblicati</div>
              <div className="text-2xl font-bold text-green-600">{stats.published}</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="text-sm text-gray-500">Bozze</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Cerca articoli..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F8B120] focus:border-transparent"
                />
              </div>

              {/* Filters */}
              <div className="flex items-center space-x-2">
                <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
                  Tutti
                </FilterButton>
                <FilterButton active={filter === 'published'} onClick={() => setFilter('published')}>
                  Pubblicati
                </FilterButton>
                <FilterButton active={filter === 'draft'} onClick={() => setFilter('draft')}>
                  Bozze
                </FilterButton>
              </div>

              {/* New Article Button */}
              <Link
                to="/admin/articles/new"
                className="px-4 py-2 bg-[#F8B120] text-white rounded-lg hover:bg-[#e5a11d] transition-colors flex items-center shrink-0"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nuovo Articolo
              </Link>
            </div>
          </div>

          {/* Posts List */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F8B120]"></div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              {searchQuery || filter !== 'all' ? (
                <>
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Nessun risultato</h3>
                  <p className="mt-2 text-gray-500">Prova a modificare i filtri di ricerca</p>
                </>
              ) : (
                <>
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">Nessun articolo</h3>
                  <p className="mt-2 text-gray-500">Inizia creando il tuo primo articolo</p>
                  <Link
                    to="/admin/articles/new"
                    className="mt-4 inline-flex items-center px-4 py-2 bg-[#F8B120] text-white rounded-lg hover:bg-[#e5a11d] transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Crea Articolo
                  </Link>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredPosts.map(post => (
                <ArticleCard
                  key={post.id}
                  post={post}
                  views={analytics[post.id]?.total || 0}
                  todayViews={analytics[post.id]?.today || 0}
                  trend={analytics[post.id]?.trend}
                  onTogglePublish={() => handleTogglePublish(post)}
                  onDelete={() => handleDelete(post.id, post.title)}
                />
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  )
}

// Filter Button Component
function FilterButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-[#F8B120] text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {children}
    </button>
  )
}

// Article Card Component
function ArticleCard({
  post,
  views,
  todayViews,
  trend,
  onTogglePublish,
  onDelete,
}: {
  post: BlogPost
  views: number
  todayViews: number
  trend?: 'up' | 'down' | 'stable'
  onTogglePublish: () => void
  onDelete: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="flex">
        {/* Image */}
        <div className="w-48 h-36 shrink-0 bg-gray-100">
          {post.image ? (
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                  {post.category}
                </span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  post.published
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {post.published ? 'Pubblicato' : 'Bozza'}
                </span>
                {!post.seo?.metaTitle && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                    SEO mancante
                  </span>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">{post.title}</h3>
              <p className="text-sm text-gray-500">/blog/{post.slug}</p>
            </div>

            {/* Views Badge */}
            <div className="flex items-center space-x-2 ml-4">
              <div className="text-right">
                <div className="flex items-center justify-end space-x-1">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm font-semibold text-gray-700">{views.toLocaleString('it-IT')}</span>
                  {trend && trend !== 'stable' && (
                    <span className={`text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {trend === 'up' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                {todayViews > 0 && (
                  <div className="text-xs text-gray-400">+{todayViews} oggi</div>
                )}
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 line-clamp-2 flex-1">{post.excerpt}</p>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center text-xs text-gray-500 space-x-4">
              <span>{post.date}</span>
              <span>{post.readTime} min lettura</span>
              <span>{post.author}</span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={onTogglePublish}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  post.published
                    ? 'text-yellow-700 hover:bg-yellow-50'
                    : 'text-green-700 hover:bg-green-50'
                }`}
              >
                {post.published ? 'Rimuovi' : 'Pubblica'}
              </button>
              <Link
                to={`/admin/articles/${post.id}/edit`}
                className="px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded transition-colors"
              >
                Modifica
              </Link>
              <button
                onClick={onDelete}
                className="px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 rounded transition-colors"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function AdminArticles() {
  return (
    <ProtectedRoute>
      <ArticlesContent />
    </ProtectedRoute>
  )
}
